import React, { useCallback, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  Flex,
  FlexItem,
  Spinner,
  Switch,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import type { UseRenewalAutopayReturn } from '../../../hooks/useRenewalAutopay';
import type { RenewalAutopayUpdateRequest } from '../../../types/models/Wallet';
import type { ApiResponseRenewalAutopay } from '../../../types/api/ApiResponseWallet';
import { formatDateTime } from '../../../utils/dateFormat';

const HINT_ID = 'renewal-autopay-hint';

interface RenewalAutopayCardProps {
  /** The parent's `useRenewalAutopay()`, so a saved-card change can refresh `has_saved_card`. */
  autopayState: UseRenewalAutopayReturn;
  /**
   * The organization's opt-in to automatic payment of all invoices, which also pays renewal invoices;
   * `null` while unknown (not loaded, unavailable or failed), in which case nothing extra is shown.
   */
  invoiceAutopayEnabled?: boolean | null;
}

/**
 * "Automatic renewal payment" — the organization's opt-in to having its domain renewal invoices
 * paid automatically from expiry − 7 days (wallet.md, "Automatic payment of domain renewals"). The
 * switch saves immediately. The renewal workflow also pays them, on the same calendar, under the
 * opt-in to automatic payment of all invoices, so the card says so when that one is on: the switch
 * alone then doesn't decide. The backend refuses the change under an impersonation session, so the
 * switch is disabled then; staff still see the current setting.
 */
const RenewalAutopayCard: React.FunctionComponent<RenewalAutopayCardProps> = ({
  autopayState,
  invoiceAutopayEnabled = null,
}) => {
  const { autopay, loading, error, refetch, setAutopay } = autopayState;
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { isImpersonating } = useImpersonation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (enabled: boolean) => {
      setIsSubmitting(true);
      try {
        const request: RenewalAutopayUpdateRequest = { enabled };
        const response = await apiFetch('/clients/renewal-autopay', {
          method: 'PUT',
          body: JSON.stringify(request),
        });
        // JSend: failures arrive as status:'error' at HTTP 200 with a display-safe message.
        const body = (await response.json().catch(() => null)) as ApiResponseRenewalAutopay | null;
        if (body?.status !== 'success' || !body.data?.renewal_autopay) {
          throw new Error(body?.data?.enabled ?? body?.message ?? `The request failed (HTTP ${response.status}).`);
        }

        setAutopay(body.data.renewal_autopay);
        addNotification({
          title: enabled ? 'Automatic renewal payment turned on' : 'Automatic renewal payment turned off',
          srTitle: 'Success alert',
          description: enabled
            ? 'Domain renewal invoices will be paid automatically from 7 days before expiry.'
            : invoiceAutopayEnabled === true
              ? 'Domain renewal invoices are still paid automatically, because automatic payment of all invoices is on.'
              : 'Domain renewal invoices will no longer be paid automatically.',
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not update automatic renewal payment',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, setAutopay, invoiceAutopayEnabled],
  );

  const renderBody = () => {
    if (loading && autopay === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the automatic payment setting" />
        </Bullseye>
      );
    }
    if (error && autopay === null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Unable to load the automatic payment setting"
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }
    if (autopay === null) {
      return null;
    }
    return (
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
        <FlexItem>
          <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem>
              <Switch
                id="renewal-autopay"
                label="Pay my domain renewals automatically"
                aria-describedby={HINT_ID}
                isChecked={autopay.enabled}
                isDisabled={isImpersonating || isSubmitting}
                onChange={(_event, checked) => void submit(checked)}
              />
            </FlexItem>
            {isSubmitting && (
              <FlexItem>
                <Spinner size="sm" aria-label="Updating automatic renewal payment" />
              </FlexItem>
            )}
          </Flex>
          <Content component="small" id={HINT_ID} className="pf-v6-u-display-block pf-v6-u-mt-sm">
            From 7 days before a domain expires, we pay its renewal invoice with your prepaid balance if it covers the
            whole invoice, otherwise with your default saved card. A renewal invoice still unpaid 3 days before expiry
            lets the domain expire.
          </Content>
          {autopay.enabled && invoiceAutopayEnabled === true && (
            <Content component="small" className="pf-v6-u-display-block pf-v6-u-mt-xs">
              Automatic payment of all invoices is also on: turning this switch off would not stop renewal payments.
            </Content>
          )}
          {autopay.changed_at !== null && (
            <Content component="small" className="pf-v6-u-display-block pf-v6-u-mt-xs">
              Last changed {formatDateTime(autopay.changed_at)}.
            </Content>
          )}
        </FlexItem>

        {!autopay.enabled && invoiceAutopayEnabled === true && (
          <FlexItem>
            <Alert variant="info" isInline title="Covered by automatic payment of all invoices">
              Your organization pays all its invoices automatically (below), so your domain renewal invoices are still
              paid automatically from 7 days before expiry, even with this switch off.
            </Alert>
          </FlexItem>
        )}

        {autopay.enabled && !autopay.has_saved_card && (
          <FlexItem>
            <Alert variant="warning" isInline title="No saved card">
              Automatic payment relies on your prepaid balance alone: a renewal invoice the balance doesn&apos;t fully
              cover won&apos;t be paid. Add a card, or keep the balance topped up.
            </Alert>
          </FlexItem>
        )}

        {isImpersonating && (
          <FlexItem>
            <Content component="small">
              Only the organization itself can change this setting, not an impersonation session.
            </Content>
          </FlexItem>
        )}
      </Flex>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Automatic renewal payment</CardTitle>
      </CardHeader>
      <CardBody>{renderBody()}</CardBody>
    </Card>
  );
};

export default RenewalAutopayCard;
